/**
=========================================================
* Soft UI Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Styles
import boxShadows from "assets/theme/base/boxShadows";
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";

// Soft UI Dashboard PRO React Helper Functions
import pxToRem from "assets/theme/functions/pxToRem";

const { size } = typography;
const { buttonBoxShadow } = boxShadows;

const getButtonColors = (mode) => {
  const palette = colors[mode] || colors.light;
  return {
    white: palette.white,
    text: palette.text,
    info: palette.info,
    secondary: palette.secondary,
    background: palette.background,
    primary: palette.primary,
    success: palette.success,
    warning: palette.warning,
    error: palette.error,
    grey: palette.grey,
  };
};

export default {
  base: ({ theme }) => {
    const mode = theme?.palette?.mode || "light";
    const isDark = mode === "dark";
    const buttonColors = getButtonColors(mode);

    return {
      backgroundColor: isDark ? buttonColors.grey[200] : buttonColors.white.main,
      minHeight: pxToRem(40),
      color: isDark ? buttonColors.white.main : buttonColors.text.primary,
      border: isDark ? "1px solid rgba(255,255,255,0.12)" : "none",
      boxShadow: isDark ? "0 2px 4px rgba(0,0,0,0.3)" : buttonBoxShadow.main,
      padding: `${pxToRem(12)} ${pxToRem(24)}`,
      fontWeight: 600,

      "&:hover": {
        backgroundColor: isDark ? buttonColors.grey[300] : buttonColors.white.main,
        boxShadow: isDark ? "0 4px 8px rgba(0,0,0,0.4)" : buttonBoxShadow.stateOf,
        opacity: 1,
      },

      "&:focus": {
        boxShadow: isDark ? "0 4px 8px rgba(0,0,0,0.4)" : buttonBoxShadow.stateOf,
      },

      "&:active, &:active:focus, &:active:hover": {
        opacity: 0.85,
        boxShadow: isDark ? "0 2px 4px rgba(0,0,0,0.3)" : buttonBoxShadow.stateOf,
      },

      "&:disabled": {
        opacity: 0.5,
        boxShadow: "none",
        cursor: "not-allowed",
      },

      "& .material-icon, .material-icons-round, svg": {
        fontSize: `${pxToRem(16)} !important`,
      },
    };
  },

  small: {
    minHeight: pxToRem(32),
    padding: `${pxToRem(8)} ${pxToRem(32)}`,
    fontSize: size.xs,

    "& .material-icon, .material-icons-round, svg": {
      fontSize: `${pxToRem(12)} !important`,
    },
  },

  large: {
    minHeight: pxToRem(47),
    padding: `${pxToRem(14)} ${pxToRem(64)}`,
    fontSize: size.sm,

    "& .material-icon, .material-icons-round, svg": {
      fontSize: `${pxToRem(22)} !important`,
    },
  },

  primary: ({ theme }) => {
    const mode = theme?.palette?.mode || "light";
    const buttonColors = getButtonColors(mode);

    return {
      backgroundColor: buttonColors.primary.main,
      color: buttonColors.white.main,

      "&:hover": {
        backgroundColor: buttonColors.primary.focus,
      },

      "&:focus:not(:hover)": {
        backgroundColor: buttonColors.primary.focus,
        boxShadow: `0 0 0 3px ${buttonColors.primary.main}40`,
      },
    };
  },

  secondary: ({ theme }) => {
    const mode = theme?.palette?.mode || "light";
    const buttonColors = getButtonColors(mode);

    return {
      backgroundColor: buttonColors.secondary.main,
      color: buttonColors.white.main,

      "&:hover": {
        backgroundColor: buttonColors.secondary.focus,
      },

      "&:focus:not(:hover)": {
        backgroundColor: buttonColors.secondary.focus,
        boxShadow: `0 0 0 3px ${buttonColors.secondary.main}40`,
      },
    };
  },

  error: ({ theme }) => {
    const mode = theme?.palette?.mode || "light";
    const buttonColors = getButtonColors(mode);

    return {
      backgroundColor: buttonColors.error.main,
      color: buttonColors.white.main,
      border: "none",

      "&:hover": {
        backgroundColor: buttonColors.error.focus,
      },

      "&:focus:not(:hover)": {
        backgroundColor: buttonColors.error.focus,
        boxShadow: `0 0 0 3px ${buttonColors.error.main}40`,
      },
    };
  },
};
